import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useToasts } from '../../lib/toast';
import type { PromoKind } from '@taj/types';

type Row = { id?: string; name: string; kind: PromoKind; value: string };

const eurToCents = (s: string) => Math.round((parseFloat(s.replace(',', '.')) || 0) * 100);
const centsToEur = (c: number) => (c / 100).toFixed(2);

export function PromotionsManager({ onBack }: { onBack: () => void }) {
  const push = useToasts((s) => s.push);
  const [enabled, setEnabled] = useState(true);
  const [promos, setPromos] = useState<Row[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getPromoConfig().then((c) => {
      setEnabled(c.enabled);
      setPromos(c.promos.map((p) => ({ id: p.id, name: p.name, kind: p.kind, value: p.kind === 'AMOUNT' ? centsToEur(p.value) : String(p.value) })));
    }).catch((e) => push(e.message, 'warn'));
  }, [push]);

  const toggleEnabled = async () => {
    const v = !enabled;
    setEnabled(v);
    try { await api.updatePromoConfig({ enabled: v }); push(v ? '🏷️ Promotions activées' : '⛔ Promotions désactivées'); }
    catch (e: any) { push(e.message, 'warn'); setEnabled(!v); }
  };

  const set = (i: number, patch: Partial<Row>) => setPromos(promos.map((p, j) => (j === i ? { ...p, ...patch } : p)));

  const save = async () => {
    setSaving(true);
    try {
      const c = await api.updatePromoConfig({
        enabled,
        promos: promos
          .map((p) => ({ id: p.id, name: p.name.trim(), kind: p.kind, value: p.kind === 'AMOUNT' ? eurToCents(p.value) : (parseInt(p.value, 10) || 0) }))
          .filter((p) => p.name && p.value > 0),
      });
      setPromos(c.promos.map((p) => ({ id: p.id, name: p.name, kind: p.kind, value: p.kind === 'AMOUNT' ? centsToEur(p.value) : String(p.value) })));
      push('✅ Promotions enregistrées');
    } catch (e: any) { push(e.message, 'warn'); }
    finally { setSaving(false); }
  };

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>🏷️ Promotions & remises</div>
        <div className="al-toggle" style={{ padding: '6px 12px', margin: 0, gap: 8 }}>
          <span className="an" style={{ fontSize: '.82rem', fontWeight: 800, color: enabled ? 'var(--green)' : 'var(--ink-faint)' }}>{enabled ? 'Activées' : 'Désactivées'}</span>
          <div className={`al-sw${enabled ? ' on' : ''}`} onClick={toggleEnabled} />
        </div>
      </div>
      {!enabled && <div className="cc" style={{ marginBottom: 14, background: '#fdecec', border: '1px solid #f2b8b8', color: '#c33', fontWeight: 700, fontSize: '.85rem' }}>⛔ Promotions désactivées : les remises ne sont pas proposées en caisse.</div>}

      <div className="cc" style={{ maxWidth: 620 }}>
        <div style={{ fontSize: '.82rem', color: 'var(--ink-faint)', marginBottom: 12 }}>
          Crée tes remises. Chacune est en <b>pourcentage (%)</b> ou en <b>montant (€)</b>. Elles seront proposées en
          caisse pour les appliquer à une commande.
        </div>
        {promos.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <input className="inp" style={{ flex: '2 1 160px', minWidth: 130 }} placeholder="Nom (ex. Happy Hour, Étudiant…)" value={p.name}
              onChange={(e) => set(i, { name: e.target.value })} />
            <select className="inp" style={{ width: 110 }} value={p.kind} onChange={(e) => set(i, { kind: e.target.value as PromoKind })}>
              <option value="PERCENT">Pourcentage %</option>
              <option value="AMOUNT">Montant €</option>
            </select>
            <input className="inp" inputMode="decimal" style={{ width: 80 }} placeholder={p.kind === 'AMOUNT' ? '€' : '%'} value={p.value}
              onChange={(e) => set(i, { value: e.target.value })} />
            <span style={{ color: 'var(--ink-dim)', fontWeight: 700 }}>{p.kind === 'AMOUNT' ? '€' : '%'}</span>
            <button type="button" title="Supprimer" onClick={() => setPromos(promos.filter((_, j) => j !== i))}
              style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.05rem' }}>🗑️</button>
          </div>
        ))}
        {promos.length === 0 && <div style={{ fontSize: '.85rem', color: 'var(--ink-faint)', marginBottom: 8 }}>Aucune promotion. Ajoute ta première remise ci-dessous.</div>}
        <button type="button" onClick={() => setPromos([...promos, { name: '', kind: 'PERCENT', value: '' }])}
          style={{ marginTop: 4, padding: '8px 12px', borderRadius: 10, border: '1px dashed var(--gold-line)', background: 'var(--gold-soft)', color: 'var(--gold)', fontWeight: 800, fontSize: '.82rem', cursor: 'pointer' }}>
          ➕ Ajouter une promotion
        </button>
        <div><button className="btn-gold" style={{ marginTop: 14 }} disabled={saving} onClick={save}>💾 ENREGISTRER</button></div>
      </div>
    </div>
  );
}
